import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import CategoryChips, { buildGroups } from '../components/CategoryChips';

const QuizList = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  // '' means "all" at each level.
  const [catFilter, setCatFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuizzes();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await api.get('/quizzes');
      setQuizzes(response.data);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (quiz) => {
    try {
      await api.put(`/quizzes/${quiz.id}`, { is_active: !quiz.is_active });
      setQuizzes(quizzes.map(q =>
        q.id === quiz.id ? { ...q, is_active: !q.is_active } : q
      ));
      setOpenMenu(null);
    } catch (error) {
      console.error('Failed to update quiz:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await api.delete(`/quizzes/${deleteModal.id}`);
      setQuizzes(quizzes.filter(q => q.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (error) {
      console.error('Failed to delete quiz:', error);
    }
  };

  const copyToClipboard = (text, quizId, type) => {
    navigator.clipboard.writeText(text);
    setCopiedId(`${quizId}-${type}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  const catOf = q => q.category_name || 'Uncategorised';
  const groups = buildGroups(quizzes, { keyOf: catOf, labelOf: catOf, subOf: q => q.subcategory });

  // With one visible category the chips treat it as chosen, so filter the same way.
  const activeCat = catFilter || (groups.length === 1 ? groups[0].key : '');
  const visible = quizzes.filter(q => {
    if (activeCat && catOf(q) !== activeCat) return false;
    if (subFilter && q.subcategory !== subFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <h1>Quizzes</h1>
        <Link to="/quizzes/new" className="btn btn-primary">
          + Create New Quiz
        </Link>
      </div>

      <CategoryChips
        groups={groups}
        total={quizzes.length}
        category={catFilter}
        subcategory={subFilter}
        onChange={(c, sub) => { setCatFilter(c); setSubFilter(sub); }}
      />

      {quizzes.length === 0 ? (
        <div className="card empty-state">
          <h3>No quizzes yet</h3>
          <p>Create your first quiz to get started</p>
          <Link to="/quizzes/new" className="btn btn-primary mt-3">Create Quiz</Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {visible.map(quiz => (
            <div
              key={quiz.id}
              className="card"
              style={{
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                transition: 'box-shadow 0.2s',
                cursor: 'pointer'
              }}
              onClick={() => navigate(`/quizzes/${quiz.id}`)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
            >
              {/* Status indicator */}
              <div style={{
                width: '8px',
                height: '60px',
                borderRadius: '4px',
                background: quiz.is_active ? '#10b981' : '#ef4444',
                flexShrink: 0
              }} />

              {/* Main content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex flex-center gap-3" style={{ marginBottom: '6px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{quiz.title}</h3>
                  <span className={`badge ${quiz.is_active ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '11px' }}>
                    {quiz.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {quiz.category_name && (
                    <span className="badge" style={{ fontSize: '11px', backgroundColor: '#ede9fe', color: '#5b21b6' }}>
                      {quiz.category_name}{quiz.subcategory ? ` › ${quiz.subcategory}` : ''}
                    </span>
                  )}
                </div>
                {quiz.description && (
                  <p className="text-muted" style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                    {quiz.description.length > 80 ? quiz.description.substring(0, 80) + '...' : quiz.description}
                  </p>
                )}
                <div className="flex gap-4" style={{ fontSize: '13px', color: '#6b7280' }}>
                  <span>{quiz.question_count || 0} questions</span>
                  <span>{quiz.time_limit_minutes} min</span>
                  <span>{quiz.attempt_count || 0} attempts</span>
                </div>
              </div>

              {/* Access code */}
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <code style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  {quiz.access_code}
                </code>
                <button
                  onClick={() => copyToClipboard(quiz.access_code, quiz.id, 'code')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: copiedId === `${quiz.id}-code` ? '#10b981' : '#6b7280',
                    fontSize: '14px'
                  }}
                  title="Copy code"
                >
                  {copiedId === `${quiz.id}-code` ? '✓' : '📋'}
                </button>
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/exam?code=${quiz.access_code}`, quiz.id, 'link')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: copiedId === `${quiz.id}-link` ? '#10b981' : '#6b7280',
                    fontSize: '14px'
                  }}
                  title="Copy exam link"
                >
                  {copiedId === `${quiz.id}-link` ? '✓' : '🔗'}
                </button>
              </div>

              {/* Actions dropdown */}
              <div style={{ position: 'relative' }} ref={openMenu === quiz.id ? menuRef : null} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => setOpenMenu(openMenu === quiz.id ? null : quiz.id)}
                  style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ⋮
                </button>

                {openMenu === quiz.id && (
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '150px',
                    zIndex: 100,
                    overflow: 'hidden'
                  }}>
                    <Link
                      to={`/quizzes/${quiz.id}`}
                      style={{
                        display: 'block',
                        padding: '10px 16px',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={e => e.target.style.background = '#f9fafb'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      Edit Quiz
                    </Link>
                    <Link
                      to={`/quizzes/${quiz.id}/results`}
                      style={{
                        display: 'block',
                        padding: '10px 16px',
                        color: '#374151',
                        textDecoration: 'none',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={e => e.target.style.background = '#f9fafb'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      View Results
                    </Link>
                    <button
                      onClick={() => toggleStatus(quiz)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        background: 'white',
                        border: 'none',
                        textAlign: 'left',
                        color: quiz.is_active ? '#dc2626' : '#059669',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={e => e.target.style.background = '#f9fafb'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      {quiz.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => { setDeleteModal(quiz); setOpenMenu(null); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        background: 'white',
                        border: 'none',
                        textAlign: 'left',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={e => e.target.style.background = '#fef2f2'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setDeleteModal(null))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Quiz</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete "<strong>{deleteModal.title}</strong>"?</p>
              <p className="text-muted mt-2">This will also delete all questions and attempt records. This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizList;
